/**
 * Utility functions for formatting and displaying medication activities
 */

export interface ActivityTimestamp {
  toDate?: () => Date
  seconds?: number
}

/**
 * Formats a timestamp (Firestore Timestamp, Date, or number) to a time string
 */
export const formatActivityTime = (timestamp: ActivityTimestamp | Date | null | undefined): string => {
  if (!timestamp) return ''

  let date: Date
  if (typeof timestamp === 'object' && 'toDate' in timestamp && timestamp.toDate) {
    // Firestore Timestamp
    date = timestamp.toDate()
  } else if (typeof timestamp === 'object' && 'seconds' in timestamp && timestamp.seconds) {
    // Firestore Timestamp object
    date = new Date(timestamp.seconds * 1000)
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else {
    date = new Date(timestamp as any)
  }

  return date.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  })
}

/**
 * Formats a date to a relative date string (Hoy, Ayer, or formatted date)
 */
export const formatActivityDate = (date: Date): string => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  
  if (date.toDateString() === today.toDateString()) {
    return 'Hoy'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Ayer'
  } else {
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short'
    })
  }
}

/**
 * Gets the status text label for an activity
 */
export const getActivityStatusText = (status: string, type?: string): string => {
  // Handle both status and type fields
  const effectiveStatus = status || type || ''
  
  switch (effectiveStatus) {
    case 'taken':
    case 'medication_taken':
      return 'Tomado'
    case 'missed':
      return 'Perdido'
    case 'skipped':
      return 'Omitido'
    case 'stock_adjusted':
    case 'stock_adjustment':
      return 'Stock ajustado'
    default:
      return effectiveStatus || 'Actividad registrada'
  }
}

/**
 * Gets the activity description text
 */
export const getActivityDescription = (
  activity: {
    type?: string
    status?: string
    scheduledTime?: string
    timestamp?: ActivityTimestamp | Date | null
    stockBefore?: number
    stockAfter?: number
  }
): string => {
  const type = activity.type || activity.status || ''
  const timestamp = activity.timestamp

  if (type === 'medication_taken' || activity.status === 'taken') {
    const timeStr = activity.scheduledTime ? ` (${activity.scheduledTime})` : ''
    const formattedTime = formatActivityTime(timestamp)
    return `Medicamento tomado a las ${formattedTime}${timeStr}`
  } else if (type === 'stock_adjustment' || activity.status === 'stock_adjusted') {
    const beforeStock = activity.stockBefore || 0
    const afterStock = activity.stockAfter || 0
    const change = afterStock - beforeStock
    const changeStr = change > 0 ? `+${change}` : `${change}`
    return `Stock ajustado: ${changeStr} (${beforeStock} → ${afterStock})`
  }

  return 'Actividad registrada'
}

